import React from 'react';

import { Link } from '~/lib/react-router-dom';

import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';

import ApprovalQueueCard from './ApprovalQueueCard';
import { CHECKLIST_STATUS_STYLES } from './constants';
import type { ApprovalQueueItem, ApprovalReportSummary } from './types';

interface Props {
  expandedQueueItems: Record<string, boolean>;
  items: Array<ApprovalQueueItem>;
  needsApprovalCount: number;
  onApprovalChange: (rowId: string) => void;
  onToggleDetails: (itemId: string) => void;
  reportSummaries: Array<ApprovalReportSummary>;
}

export default function ApprovalQueue({
  expandedQueueItems,
  items,
  needsApprovalCount,
  onApprovalChange,
  onToggleDetails,
  reportSummaries,
}: Props) {
  const hasOutstandingApprovals = needsApprovalCount > 0;
  const approvalItemCopy = `checklist item${needsApprovalCount === 1 ? '' : 's'}`;
  const approvalReviewVerb = needsApprovalCount === 1 ? 'needs' : 'need';

  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          {hasOutstandingApprovals ? <WarningAmberIcon color="warning" /> : <CheckIcon color="success" />}
          <Typography variant="body2">
            {hasOutstandingApprovals ? (
              <>
                <strong>Require approval:</strong> {needsApprovalCount} {approvalItemCopy} {approvalReviewVerb} review.
              </>
            ) : (
              <strong>Nothing requires approval.</strong>
            )}
          </Typography>
        </Stack>
        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
          {reportSummaries.length === 0 ? (
            <Paper variant="outlined" sx={{ color: 'text.secondary', p: 3, textAlign: 'center' }}>
              Nothing requires approval.
            </Paper>
          ) : (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
              {reportSummaries.map((summary) => (
                <Paper
                  key={summary.categoryId}
                  variant="outlined"
                  sx={{
                    maxWidth: 280,
                    minWidth: 240,
                    p: 2,
                    ...CHECKLIST_STATUS_STYLES.approvalOverviewCard,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700}>
                    {summary.categoryLabel}
                  </Typography>
                  <Typography variant="h4" color="warning.dark" fontWeight={700} sx={{ my: 0.75 }}>
                    {summary.count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {summary.description}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                    {summary.sourceLinks.map((sourceLink) => {
                      const isExternalLink = sourceLink.href.startsWith('http');

                      return (
                        <Button
                          key={`${summary.categoryId}:${sourceLink.href}`}
                          color="warning"
                          component={isExternalLink ? 'a' : Link}
                          href={isExternalLink ? sourceLink.href : undefined}
                          rel="noreferrer"
                          size="small"
                          sx={{ px: 0 }}
                          target="_blank"
                          to={isExternalLink ? undefined : sourceLink.href}
                          endIcon={<OpenInNewIcon />}
                        >
                          {sourceLink.label}
                        </Button>
                      );
                    })}
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Box>
      </Paper>

      <Stack spacing={1.5}>
        {items.length === 0 ? (
          <Paper variant="outlined" sx={{ color: 'text.secondary', p: 4, textAlign: 'center' }}>
            {hasOutstandingApprovals ? 'No approval items match this view.' : 'Nothing requires approval.'}
          </Paper>
        ) : (
          items.map((queueItem) => (
            <ApprovalQueueCard
              key={queueItem.id}
              expanded={!!expandedQueueItems[queueItem.id]}
              queueItem={queueItem}
              onApprovalChange={onApprovalChange}
              onToggleDetails={onToggleDetails}
            />
          ))
        )}
      </Stack>
    </Stack>
  );
}
